import { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  DEFAULT_UI_LOCALE,
  SUPPORTED_UI_LOCALES,
  isSupportedUILocale,
  persistUILocale,
  resolveUILocale,
  type UILocale,
} from '../locale';

/**
 * Read the current UI locale from route params. Assumes the route was
 * mounted under `/:uiLocale/...` and the param has already been validated
 * by <LegacyLocaleRedirect>.
 */
export function useUILocale(): UILocale {
  const { uiLocale } = useParams<{ uiLocale?: string }>();
  return isSupportedUILocale(uiLocale) ? uiLocale : DEFAULT_UI_LOCALE;
}

/**
 * When mounted at a path without a valid UI locale, redirect to the same
 * path prefixed with the preferred locale. Used to handle legacy URLs
 * (`/login`, `/explore`, shares issued before localization) and to recover
 * from unsupported-locale URLs (`/de/login` when only `en` ships) without
 * double-prefixing into `/en/de/login`.
 */
export const LegacyLocaleRedirect: React.FC = () => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  // If the first segment looks like a locale code but isn't supported,
  // drop it so the redirect lands on a real page, not /en/de/login.
  if (segments[0] && /^[a-z]{2}$/.test(segments[0]) && !isSupportedUILocale(segments[0])) {
    segments.shift();
  }
  const preferred = resolveUILocale(null);
  const rebuilt = segments.length > 0 ? `/${segments.join('/')}` : '';
  return <Navigate to={`/${preferred}${rebuilt}${location.search}${location.hash}`} replace />;
};

/**
 * Persist the currently-active UI locale from URL so it sticks across sessions.
 */
export function useSyncLocaleFromUrl(): void {
  const locale = useUILocale();
  useEffect(() => {
    persistUILocale(locale);
  }, [locale]);
}

export { SUPPORTED_UI_LOCALES };
